import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import axiosInstance from '@/api/axiosInstance';
import { queryClient } from '@/api/queryClient';
import { Toast, type IToast } from '@/components/common';
import { visibleScreens } from '@/constants/permissions';
import { ROUTES } from '@/constants/routes';
import { useSignOut } from '@/features/auth/hooks/useSignOut';
import { MobilePinModal } from '@/features/mobilePin/MobilePinModal';
import { dropToken } from '@/lib/auth/session';
import { clearAuthToken } from '@/lib/http';
import { dateTime, initials } from '@/lib/utils/format';
import { useAuthStore } from '@/stores/authStore';
import logo from '@/assets/logo.png';

export function AppLayout() {
  const navigate = useNavigate();
  const admin = useAuthStore((state) => state.admin);
  const signOutOfStore = useAuthStore((state) => state.signOut);
  const { mutate: signOut } = useSignOut();

  const leave = () => {
    const finish = () => {
      clearAuthToken(axiosInstance);
      dropToken();
      queryClient.clear();
      signOutOfStore();
      navigate(ROUTES.login, { replace: true });
    };
    signOut(undefined, { onSuccess: finish, onError: finish });
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <aside className="flex w-52 shrink-0 flex-col gap-6 border-r border-gray-200 bg-white px-4 py-5">
        <div className="flex flex-col gap-1">
          <img src={logo} alt="SimplySmart" className="h-7 object-contain" />
          <span className="text-xs uppercase tracking-wide text-gray-400">Platform</span>
        </div>
        <nav className="flex flex-col gap-1">
          {visibleScreens(admin).map((item) => (
            <NavLink
              key={item.route}
              to={item.route}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm ${
                  isActive
                    ? 'bg-blue-600 font-medium text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-gray-200 bg-white px-6 py-3">
          <span className="text-lg font-semibold text-gray-900">SimplySmart</span>
          <span className="rounded bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            Platform
          </span>
          <div className="ml-auto">
            <ProfileMenu onSignOut={leave} />
          </div>
        </header>

        <main className="min-w-0 flex-1 px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// The signed-in admin's menu: Mobile PIN (only with a mobile on the account) and Sign out.
function ProfileMenu({ onSignOut }: { onSignOut: () => void }) {
  const admin = useAuthStore((state) => state.admin);
  const [open, setOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [toast, setToast] = useState<IToast | null>(null);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!menu.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={menu} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm text-gray-700 hover:bg-gray-100"
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
          {initials(admin?.name)}
        </span>
        {admin?.name}
        <span aria-hidden="true" className="text-xs text-gray-400">
          ▾
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1.5 flex w-60 flex-col rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
        >
          <div className="mb-1 border-b border-gray-100 px-2.5 py-2">
            <span className="block text-sm font-medium text-gray-900">{admin?.name}</span>
            <span className="block text-xs text-gray-500">{admin?.email}</span>
          </div>
          {admin?.mobile && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setPinOpen(true);
              }}
              className="rounded-md px-2.5 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              Mobile PIN
              <span className="block text-xs text-gray-500">
                {admin.mobile_pin_set_at
                  ? `Set ${dateTime(admin.mobile_pin_set_at)} · ${admin.mobile}`
                  : `Not set · ${admin.mobile}`}
              </span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className="rounded-md px-2.5 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            Sign out
          </button>
        </div>
      )}
      {pinOpen && (
        <MobilePinModal
          onClose={() => setPinOpen(false)}
          onSaved={() => {
            setPinOpen(false);
            setToast({
              tone: 'success',
              title: 'Mobile PIN saved',
              message: `Use it with ${admin?.mobile ?? 'your mobile'} in the resident app`,
            });
          }}
        />
      )}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
